<template>
  <div class="h-auto form-control">
    <div class="editor-header d-flex justify-content-between">
      <div v-if="editor" class="d-flex editor-actions">
        <bold-action :editor="editor"></bold-action>
        <italic-action :editor="editor"></italic-action>
        <strike-action :editor="editor"></strike-action>
        <underline-action :editor="editor"></underline-action>
        <code-action :editor="editor"></code-action>

        <div class="vr mx-1"></div>

        <paragraph-action :editor="editor"></paragraph-action>
        <font-size-action :editor="editor"></font-size-action>
        <headlines-action :editor="editor"></headlines-action>
        <color-action :editor="editor"></color-action>

        <button
          class="col flex-grow-0 border mx-1"
          style="display: none"
          title="->"
          @click.prevent="editor.chain().focus().addIndent().run()"
        >
          ->
        </button>
      </div>
      <div class="d-flex mt-2">
        <button class="col flex-grow-0 border px-2 py-1 me-1" @click.prevent="setViewMode('wysiwyg')">WYSIWYG</button>
        <button class="col flex-grow-0 border px-2 py-1 ms-1" @click.prevent="setViewMode('plain')">Plain</button>
      </div>
    </div>
    <div class="editor-container">
      <editor-content v-if="viewMode === 'wysiwyg'" :editor="editor"></editor-content>
      <text-area-field
        v-if="viewMode === 'plain'"
        :name="name"
        :options="{ noResize: true }"
        :placeholder="label"
        :readonly="readonly"
        :required="required"
        :value="localValue"
        @update:mirror="updateContent"
        @update:data="(data) => $emit('update:data', data)"
      />
    </div>
  </div>
</template>

<script>
import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontSize } from '@tobiasafischer/tiptap-extension-font-size';
import { ref } from 'vue';
import TextAreaField from '@kanrisha/components/Elements/Form/TextArea';
import showdown from 'showdown';
import { AddIndent, Indent } from "@kanrisha/tiptap/indent";
import BoldAction from "@kanrisha/components/Elements/Form/Editor/Actions/BoldAction";
import CodeAction from "@kanrisha/components/Elements/Form/Editor/Actions/CodeAction";
import ColorAction from "@kanrisha/components/Elements/Form/Editor/Actions/ColorAction";
import FontSizeAction from "@kanrisha/components/Elements/Form/Editor/Actions/FontSizeAction";
import HeadlinesAction from "@kanrisha/components/Elements/Form/Editor/Actions/HeadlinesAction";
import ItalicAction from "@kanrisha/components/Elements/Form/Editor/Actions/ItalicAction";
import ParagraphAction from "@kanrisha/components/Elements/Form/Editor/Actions/ParagraphAction";
import StrikeAction from "@kanrisha/components/Elements/Form/Editor/Actions/StrikeAction";
import UnderlineAction from "@kanrisha/components/Elements/Form/Editor/Actions/UnderlineAction";

export default {
  components: {
    BoldAction,
    CodeAction,
    ColorAction,
    EditorContent,
    FontSizeAction,
    HeadlinesAction,
    ItalicAction,
    ParagraphAction,
    StrikeAction,
    TextAreaField,
    UnderlineAction,
  },
  props: {
    name: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    errors: { type: Array, default: null },
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    rows: { type: Number, default: 1 },
  },
  emits: ['update:data'],
  setup(props, { emit }) {
    const viewMode = ref('wysiwyg');
    const setViewMode = (mode) => {
      viewMode.value = mode;
    };
    const converter = new showdown.Converter();
    const localValue = ref(typeof props.value === 'string' ? props.value : '');

    const editor = useEditor({
      content: converter.makeHtml(localValue.value),
      extensions: [StarterKit, Underline, TextStyle, Color, FontSize, AddIndent, Indent],
      onUpdate: () => {
        localValue.value = editor.value.getHTML();
      },
      onBlur: () => {
        emit('update:data', localValue.value);
      },
    });

    const updateContent = (value) => {
      // ~#PARAGRAPH#~ is used as a temporary placeholder to prevent blank lines from being removed
      let paragraphContent = converter
        .makeHtml(value.replaceAll('\n', '~#PARAGRAPH#~\n'))
        .replaceAll('~#PARAGRAPH#~', '')
        .replaceAll('\n', '</p><p>');
      editor.value.commands.setContent(paragraphContent, false);
      localValue.value = value;
    };

    return {
      editor,
      localValue,
      setViewMode,
      updateContent,
      viewMode,
    };
  },
};
</script>

<style lang="scss" scoped>
button {
  &.active {
    background-color: var(--primary-color);
  }
}

.border {
  border: 1px solid var(--border-color) !important;
}
</style>
